import { useEffect } from 'react';

import type { Location } from 'js/lib/useRouter';

import {
  setContinuousChatSessionId,
  setContinuousChatSessionStorageKey,
} from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import {
  getContinuousChatSessionStorageKey,
  persistentContinuousChatSessionId,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/sessionUtils';

type UseFetchChatHistoryOnVisibilityChangeProps = {
  /** Whether continuous chat is enabled */
  enableContinuousChat: boolean;
  /** Function to fetch chat history */
  fetchChatHistory?: (sessionId: string) => void;
  /** Current location object */
  location: Location;
  /** Course ID */
  courseId?: string;
  /** Custom chat session key */
  chatSessionKey?: string;
};

/**
 * Hook that handles fetching chat history when user focuses on a browser tab
 * when continuous chat is enabled. This ensures chat history is up-to-date
 * if a user has a chat open in multiple tabs.
 */
export const useFetchChatHistoryOnVisibilityChange = ({
  enableContinuousChat,
  fetchChatHistory,
  location,
  courseId,
  chatSessionKey,
}: UseFetchChatHistoryOnVisibilityChangeProps) => {
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Only fetch when the page becomes visible (user focuses on the tab)
      if (!document.hidden && enableContinuousChat && fetchChatHistory) {
        const sessionStorageKey = getContinuousChatSessionStorageKey(location, courseId, chatSessionKey);
        const sanitizedChatSessionId = persistentContinuousChatSessionId.get(sessionStorageKey);
        setContinuousChatSessionStorageKey(sessionStorageKey);
        setContinuousChatSessionId(sanitizedChatSessionId);

        if (sanitizedChatSessionId) {
          fetchChatHistory(sanitizedChatSessionId);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchChatHistory, enableContinuousChat, location, courseId, chatSessionKey]);
};
